import { FC, useState, useEffect } from 'react';
import { Button, Input, Row, Col, Typography } from 'antd';
import HalfScreenModal from 'components/HalfScreenModal/HalfScreenModal';
import imageIcon from 'images/icons/image-icon.png';
import largeTextIcon from 'images/icons/large-text-icon.png';
import contentCardIcon from 'images/icons/content-card.png';
import styles from './addCustomPageComponent.module.less';

const { Text } = Typography;

interface ComponentType {
  id: string;
  name: string;
  description: string;
  icon: any;
  count: number;
}

interface Props {
  visible: boolean;
  onClose: () => void;
  onSave: (pageTitle: string, selectedComponents: ComponentType[]) => void;
  onCheckTitleExists?: (title: string) => Promise<boolean>;
  textLimit?: number;
  isAddingComponent?: boolean;
  existingPageTitle?: string;
}

const AVAILABLE_COMPONENTS: Omit<ComponentType, 'count'>[] = [
  {
    id: 'image-block',
    name: 'Image Block',
    description: 'A single, expansive image that spans across the page',
    icon: <img src={imageIcon} alt="imageIcon" />,
  },
  {
    id: 'large-text-field',
    name: 'Large Text Field',
    description: 'A text field for entering large amounts of text content',
    icon: <img src={largeTextIcon} alt="largeTextIcon" />,
  },
  {
    id: 'content-card',
    name: 'Content Card',
    description:
      'A component with a header field and text field for structured content',
    icon: <img src={contentCardIcon} alt="contentCardIcon" />,
  },
];

const AddCustomPageComponent: FC<Props> = (props: Props) => {
  const {
    visible,
    onClose,
    onSave,
    onCheckTitleExists,
    textLimit = 22,
    isAddingComponent = false,
    existingPageTitle = '',
  } = props;

  const [pageTitle, setPageTitle] = useState<string>('');
  const [components, setComponents] = useState<ComponentType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [titleExistsError, setTitleExistsError] = useState<boolean>(false);

  useEffect(() => {
    if (visible) {
      setPageTitle(isAddingComponent ? existingPageTitle : '');
      setComponents(
        AVAILABLE_COMPONENTS.map((comp) => ({ ...comp, count: 0 }))
      );
      setTitleExistsError(false);
    }
  }, [visible, isAddingComponent, existingPageTitle]);

  const handleSave = async () => {
    setIsLoading(true);
    setTitleExistsError(false);

    try {
      if (!isAddingComponent && onCheckTitleExists) {
        const titleExists = await onCheckTitleExists(pageTitle.trim());

        if (titleExists) {
          setTitleExistsError(true);
          setIsLoading(false);
          return;
        }
      }

      // Get selected components
      const selectedComponents = components.filter((comp) => comp.count > 0);

      // For component mode, use existing page title; for page mode, use entered title
      const titleToSave = isAddingComponent
        ? existingPageTitle
        : pageTitle.trim();

      await onSave(titleToSave, selectedComponents);
      onClose();
    } catch (error) {
      console.error('Error saving page:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setPageTitle(isAddingComponent ? existingPageTitle : '');
    setComponents(AVAILABLE_COMPONENTS.map((comp) => ({ ...comp, count: 0 })));
    setTitleExistsError(false);
    onClose();
  };

  const updateComponentCount = (componentId: string, increment: boolean) => {
    setComponents((prev) => {
      const updated = prev.map((comp) => {
        if (comp.id === componentId) {
          const newCount = increment
            ? comp.count + 1
            : Math.max(0, comp.count - 1);
          return { ...comp, count: newCount };
        }
        return comp;
      });
      return updated;
    });
  };

  const textCharacterLength = pageTitle.length;
  const hasSelectedComponents = components.some((comp) => comp.count > 0);

  // Different validation logic based on mode
  const isDisabled = isAddingComponent
    ? !hasSelectedComponents
    : textCharacterLength > textLimit ||
      !pageTitle.trim().length ||
      !hasSelectedComponents ||
      titleExistsError;

  const getSelectedComponentsSummary = () => {
    const selectedComponents = components.filter((comp) => comp.count > 0);

    return selectedComponents.map((comp, index) => (
      <span key={comp.id} className={styles.selectedComponentItem}>
        <span className={styles.selectedComponentIcon}>{comp.icon}</span>
        <span className={styles.selectedComponentText}>
          {comp.name} ({comp.count})
        </span>
        {index < selectedComponents.length - 1 && <span>, </span>}
      </span>
    ));
  };

  return (
    <HalfScreenModal
      width="620px"
      title={
        isAddingComponent ? 'Add Components to Page' : 'Add New Custom Page'
      }
      visible={visible}
      className={styles.modalWrapper}
      footer={[
        <Button
          className={`${styles.btn} ${styles.cancel}`}
          key="cancel"
          onClick={handleClose}
          loading={isLoading}
        >
          Cancel
        </Button>,
        <Button
          className={styles.btn}
          key="submit"
          type="primary"
          onClick={handleSave}
          disabled={isDisabled}
          loading={isLoading}
        >
          Done
        </Button>,
      ]}
      onCancel={handleClose}
    >
      {/* Only show title input in page mode */}
      {!isAddingComponent && (
        <>
          <Col xs={24} className={styles.labelWrapper}>
            <div>Page Title *</div>
          </Col>
          <Row>
            <Col xs={24}>
              <Input
                className={`${styles.input} ${
                  textCharacterLength > textLimit || titleExistsError
                    ? styles.errorInput
                    : ''
                }`}
                value={pageTitle}
                onChange={(e) => {
                  setPageTitle(e.target.value);
                  if (titleExistsError) {
                    setTitleExistsError(false);
                  }
                }}
                maxLength={100}
              />
            </Col>
          </Row>
          <Row justify="space-between" className={styles.charLimitRow}>
            <Text
              className={`${styles.charLimit} ${
                textCharacterLength > textLimit || titleExistsError
                  ? styles.errorText
                  : ''
              }`}
            >
              {titleExistsError
                ? 'Title already exists'
                : textCharacterLength > textLimit
                ? 'Character limit reached'
                : ''}
            </Text>
            <Text
              className={`${styles.charLimit} ${
                textCharacterLength > textLimit ? styles.errorText : ''
              }`}
            >
              Character Limit: {textCharacterLength}/{textLimit}
            </Text>
          </Row>
        </>
      )}

      <Col xs={24} className={styles.sectionLabelWrapper}>
        <div>Select Components for Your Page</div>
      </Col>

      {components.map((component) => (
        <div key={component.id} className={styles.componentRow}>
          <Row align="middle" justify="space-between">
            <Col flex="1">
              <div className={styles.componentInfo}>
                <div className={styles.componentHeader}>
                  <span className={styles.componentIcon}>{component.icon}</span>
                  <span className={styles.componentName}>{component.name}</span>
                </div>
                <div className={styles.componentDescription}>
                  {component.description}
                </div>
              </div>
            </Col>
            <Col>
              <div className={styles.componentControls}>
                <Button
                  className={styles.controlBtn}
                  onClick={() => updateComponentCount(component.id, false)}
                  disabled={component.count === 0}
                >
                  –
                </Button>
                <span className={styles.countDisplay}>{component.count}</span>
                <Button
                  className={styles.controlBtn}
                  onClick={() => updateComponentCount(component.id, true)}
                >
                  +
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      ))}

      <div className={styles.selectedComponentsSection}>
        <div className={styles.selectedComponentsLabel}>
          <span
            className={
              hasSelectedComponents
                ? styles.selectedComponentsTitle
                : styles.selectedComponentsDisabledTitle
            }
          >
            Selected Components:
          </span>
          {hasSelectedComponents && (
            <span className={styles.selectedComponentsList}>
              {getSelectedComponentsSummary()}
            </span>
          )}
        </div>
      </div>
    </HalfScreenModal>
  );
};

export default AddCustomPageComponent;
